import { HouseholdLayout } from '@/components/layouts/HouseholdLayout';
import { PageHeader } from '@/components/PageHeader';
import { Avatar, Box, Typography, Stack, Button, List, ListItem, ListDivider, ListSubheader, Card, CardContent } from '@mui/joy';
import { useNavigate, useParams } from 'react-router-dom';
import { auth } from '@/features/auth/firebase';
import { useGetHouseholdsQuery } from '@/features/households/api';
import { useGetProfileQuery } from '@/features/onboarding/api';
import { useAuthState } from 'react-firebase-hooks/auth';
import { sortBy } from 'lodash';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import SpokeTwoToneIcon from '@mui/icons-material/SpokeTwoTone';
import PeopleTwoToneIcon from '@mui/icons-material/PeopleTwoTone';
import CategoryTwoToneIcon from '@mui/icons-material/CategoryTwoTone';
import { useLogout } from '@/hooks/useLogout';
import { useSwitchHousehold } from '@/hooks/useSwitchHousehold';

export function MyAccountPage() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const { data: profile } = useGetProfileQuery();
  const { householdId } = useParams();
  const { data: households } = useGetHouseholdsQuery();

  const houseHoldCount = households?.data?.length;
  const userProfile = profile?.data?.profile;
  const sortedHouseholds = sortBy(households?.data ?? [], 'name');
  const isInstanceAdmin = profile?.data?.instance_role === 'admin';
  const household = households?.data?.find((h) => h.id === householdId);
  const isHouseholdAdmin = household?.role === 'admin';

  const logout = useLogout();
  const switchHousehold = useSwitchHousehold();

  if (user == null || userProfile == null) {
    return null;
  }

  return (
    <HouseholdLayout>
      <Stack gap={2} direction="column">
        <PageHeader title="My Account" subtitle="Manage your account and preferences" />
                
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar src={user.photoURL} size="lg" />
              <Box>
                <Typography level="title-lg">
                  {userProfile.display_name}
                </Typography>
                <Typography level="body-sm" textColor="text.tertiary">
                  {user.email}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <ListSubheader sx={{ mb: 1 }}>Household{houseHoldCount > 1 ? 's' : ''}</ListSubheader>
            <List>
              {sortedHouseholds.map((h) => (
                <ListItem 
                  key={h.id} 
                  onClick={() => switchHousehold(h.id)}
                  sx={{ 
                    cursor: 'pointer',
                    pl: h.id !== householdId ? '34px' : '12px',
                    '&:hover': { backgroundColor: 'var(--joy-palette-neutral-50)' }
                  }}
                >
                  {h.id === householdId && <CheckRoundedIcon sx={{ mr: 1 }} />}
                  <Typography>{h.name}</Typography>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {(isInstanceAdmin || isHouseholdAdmin) && (
          <Card>
            <CardContent>
              <ListSubheader sx={{ mb: 1 }}>Administration</ListSubheader>
              <List>
                {isInstanceAdmin && (
                  <>
                    <ListItem 
                      onClick={() => navigate(`/h/${householdId}/ingredients`)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'var(--joy-palette-neutral-50)' }
                      }}
                    >
                      <SpokeTwoToneIcon sx={{ mr: 1 }} />
                      <Typography>Ingredients</Typography>
                    </ListItem>
                    <ListItem 
                      onClick={() => navigate(`/h/${householdId}/categories`)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'var(--joy-palette-neutral-50)' }
                      }}
                    >
                      <CategoryTwoToneIcon sx={{ mr: 1 }} />
                      <Typography>Categories</Typography>
                    </ListItem>
                  </>
                )}
                {isHouseholdAdmin && (
                  <>
                    {isInstanceAdmin && <ListDivider />}
                    <ListItem 
                      onClick={() => navigate(`/h/${householdId}/users`)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'var(--joy-palette-neutral-50)' }
                      }}
                    >
                      <PeopleTwoToneIcon sx={{ mr: 1 }} />
                      <Typography>Users</Typography>
                    </ListItem>
                  </>
                )}
              </List>
            </CardContent>
          </Card>
        )}                  
        <Button
          variant="outlined"
          color="neutral"
          startDecorator={<SettingsRoundedIcon />}
          onClick={() => navigate(`/h/${householdId}/settings`)}
          fullWidth
        >
          Settings
        </Button>
        <Button
          variant="outlined"
          color="danger"
          startDecorator={<LogoutRoundedIcon />}
          onClick={logout}
          fullWidth
        >
          Log out
        </Button>          
      </Stack>
    </HouseholdLayout>
  );
}
